/**
 * Stripe Configuration
 * 
 * This file provides Stripe configuration for the frontend.
 * 
 * Note: In the frontend we use import.meta.env for environment variables.
 */

// Stripe publishable key from environment variables
export const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Type definitions
type SubscriptionLevel = 'standard' | 'premium';
type BillingCycle = 'monthly' | 'yearly';
type Currency = 'thb' | 'usd';
type ServiceType = 'flower' | 'fruit' | 'cat_grass';

// For the frontend, we use import.meta.env for environment variables
export const STRIPE_PRICE_IDS: Record<Currency, Record<SubscriptionLevel, Record<BillingCycle, string>>> = {
  thb: {
    standard: {
      monthly: import.meta.env.VITE_STRIPE_STANDARD_THB_MONTHLY_SUB_PRICE_ID || 'price_1RHJV5BaHaaFZqsCMWdogVz8', // Standard Monthly THB
      yearly: import.meta.env.VITE_STRIPE_STANDARD_THB_YEARLY_SUB_PRICE_ID || 'price_1RHJV7BaHaaFZqsCavzbYkP7'   // Standard Yearly THB
    },
    premium: {
      monthly: import.meta.env.VITE_STRIPE_PREMIUM_THB_MONTHLY_SUB_PRICE_ID || 'price_1RHJV9BaHaaFZqsCdlapy1PR', // Premium Monthly THB
      yearly: import.meta.env.VITE_STRIPE_PREMIUM_THB_YEARLY_SUB_PRICE_ID || 'price_1RHJVABaHaaFZqsCmNYsPkER'   // Premium Yearly THB
    }
  },
  usd: {
    standard: {
      monthly: import.meta.env.VITE_STRIPE_STANDARD_USD_MONTHLY_SUB_PRICE_ID || 'price_1RHJV5BaHaaFZqsCcY9i71P7', // Standard Monthly USD
      yearly: import.meta.env.VITE_STRIPE_STANDARD_USD_YEARLY_SUB_PRICE_ID || 'price_1RHJV7BaHaaFZqsCxt3dS1hw'   // Standard Yearly USD
    },
    premium: {
      monthly: import.meta.env.VITE_STRIPE_PREMIUM_USD_MONTHLY_SUB_PRICE_ID || 'price_1RHJV9BaHaaFZqsC5yECqLuY', // Premium Monthly USD
      yearly: import.meta.env.VITE_STRIPE_PREMIUM_USD_YEARLY_SUB_PRICE_ID || 'price_1RHJVBBaHaaFZqsCFeWH6pFm'   // Premium Yearly USD
    }
  }
};

// Define fruit subscription price IDs separately
export const FRUIT_STRIPE_PRICE_IDS: Record<Currency, Record<SubscriptionLevel, Record<BillingCycle, string>>> = {
  thb: {
    standard: {
      monthly: import.meta.env.VITE_STRIPE_FRUIT_STANDARD_THB_MONTHLY_SUB_PRICE_ID || 'price_1RR50qBaHaaFZqsCaDGZYlBX', // Fruit Standard Monthly THB
      yearly: import.meta.env.VITE_STRIPE_FRUIT_STANDARD_THB_YEARLY_SUB_PRICE_ID || 'price_1RR50rBaHaaFZqsCpdfXlvum'   // Fruit Standard Yearly THB
    },
    premium: {
      monthly: import.meta.env.VITE_STRIPE_FRUIT_PREMIUM_THB_MONTHLY_SUB_PRICE_ID || 'price_1RR50rBaHaaFZqsCk18E5Bfi', // Fruit Premium Monthly THB
      yearly: import.meta.env.VITE_STRIPE_FRUIT_PREMIUM_THB_YEARLY_SUB_PRICE_ID || 'price_1RR50sBaHaaFZqsCMPPdEk5O'   // Fruit Premium Yearly THB
    }
  },
  usd: {
    standard: {
      monthly: import.meta.env.VITE_STRIPE_FRUIT_STANDARD_USD_MONTHLY_SUB_PRICE_ID || 'price_1RR50qBaHaaFZqsCowm1jeXb', // Fruit Standard Monthly USD
      yearly: import.meta.env.VITE_STRIPE_FRUIT_STANDARD_USD_YEARLY_SUB_PRICE_ID || 'price_1RR50rBaHaaFZqsC4oh7FqvW'   // Fruit Standard Yearly USD
    },
    premium: {
      monthly: import.meta.env.VITE_STRIPE_FRUIT_PREMIUM_USD_MONTHLY_SUB_PRICE_ID || 'price_1RR50sBaHaaFZqsCpGgAbLmX', // Fruit Premium Monthly USD
      yearly: import.meta.env.VITE_STRIPE_FRUIT_PREMIUM_USD_YEARLY_SUB_PRICE_ID || 'price_1RR50tBaHaaFZqsCmLeXZMKq'   // Fruit Premium Yearly USD
    }
  }
};

// Define cat grass subscription price IDs separately
export const CAT_GRASS_STRIPE_PRICE_IDS: Record<Currency, Record<SubscriptionLevel, Record<BillingCycle, string>>> = {
  thb: {
    standard: {
      monthly: import.meta.env.VITE_STRIPE_CAT_GRASS_STANDARD_THB_MONTHLY_SUB_PRICE_ID || 'price_1Rgi6RBaHaaFZqsC2UGzQqgv', // Cat Grass Standard Monthly THB
      yearly: import.meta.env.VITE_STRIPE_CAT_GRASS_STANDARD_THB_YEARLY_SUB_PRICE_ID || 'price_1Rgi6SBaHaaFZqsCLLRKP5n6'   // Cat Grass Standard Yearly THB
    },
    premium: {
      monthly: import.meta.env.VITE_STRIPE_CAT_GRASS_PREMIUM_THB_MONTHLY_SUB_PRICE_ID || 'price_1Rgi6TBaHaaFZqsC5YCCXsOX', // Cat Grass Premium Monthly THB
      yearly: import.meta.env.VITE_STRIPE_CAT_GRASS_PREMIUM_THB_YEARLY_SUB_PRICE_ID || 'price_1Rgi6UBaHaaFZqsCyYCKlgq5'   // Cat Grass Premium Yearly THB
    }
  },
  usd: {
    standard: {
      monthly: import.meta.env.VITE_STRIPE_CAT_GRASS_STANDARD_USD_MONTHLY_SUB_PRICE_ID || 'price_1Rgi6SBaHaaFZqsCTNpN0Jek', // Cat Grass Standard Monthly USD
      yearly: import.meta.env.VITE_STRIPE_CAT_GRASS_STANDARD_USD_YEARLY_SUB_PRICE_ID || 'price_1Rgi6TBaHaaFZqsCA8QHMfyD'   // Cat Grass Standard Yearly USD
    },
    premium: {
      monthly: import.meta.env.VITE_STRIPE_CAT_GRASS_PREMIUM_USD_MONTHLY_SUB_PRICE_ID || 'price_1Rgi6UBaHaaFZqsCTDgwIi2n', // Cat Grass Premium Monthly USD
      yearly: import.meta.env.VITE_STRIPE_CAT_GRASS_PREMIUM_USD_YEARLY_SUB_PRICE_ID || 'price_1Rgi6VBaHaaFZqsCHEwEbXpL'   // Cat Grass Premium Yearly USD
    }
  }
};

// Helper function to get a price ID
export const getStripePriceId = (
  plan: SubscriptionLevel, 
  billingCycle: BillingCycle,
  currency: Currency = 'thb',
  serviceType: ServiceType = 'flower'
): string => {
  // Select the appropriate price ID table based on service type
  let priceTable;
  switch (serviceType) {
    case 'fruit':
      priceTable = FRUIT_STRIPE_PRICE_IDS;
      break;
    case 'cat_grass':
      priceTable = CAT_GRASS_STRIPE_PRICE_IDS;
      break;
    case 'flower':
    default:
      priceTable = STRIPE_PRICE_IDS;
      break;
  }
  return priceTable[currency][plan][billingCycle];
};
